import React, { useState, useEffect, useRef } from 'react';
import { AllUsersQuery, Privilege } from '../../api';
import { PagingState, CustomPaging, SortingState, Sorting } from '@devexpress/dx-react-grid';
import { Grid, Table, TableHeaderRow, TableColumnResizing, PagingPanel } from '@devexpress/dx-react-grid-material-ui';
import CardHeader from '../Util/CardHeader';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import styled from 'styled-components';
import UsersFilter from './UsersFilter';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import DeleteUser from '../Util/DeleteUser';
import useLocalStorage from '../Util/useLocalStorage';
import { Checkbox } from '@material-ui/core';
import CreateEditUser from './CreateEditUser';
import ResendInvite from '../Util/ResendInvite';
import useHasPrivilege from '../Util/useHasPrivilege';
import usersService from '../../services/users';

interface Props extends RouteComponentProps {
  account: string;
}

export type Diff<T, U> = T extends U ? never : T;

export type User = Diff<Diff<Diff<AllUsersQuery['allUsers'], null | undefined>['users'], null | undefined>[0], null>;

const roles = (row: User) => {
  return (row && row.roles && row?.roles.map((role) => role).join(', ')) || '-';
};

const deleteUserValue = (row: User, onClose: (reload: Boolean) => void) => {
  return <DeleteUser user={row} onClose={onClose} />;
};

const resendInviteValue = (row: User) => {
  return row?.emailVerified ? <div /> : <ResendInvite user={row} />;
};

const accountEnabled = (row: User) => {
  return <Checkbox disabled checked={row?.accountEnabled || false} />;
};

const emailVerified = (row: User) => {
  return <Checkbox disabled checked={row?.emailVerified || false} />;
};

const accountLocked = (row: User) => {
  return <Checkbox disabled checked={row?.accountLocked || false} />;
};

const sortingStateColumnExtensions = [
  { columnName: 'components', sortingEnabled: false },
  { columnName: 'runs.bootTime', sortingEnabled: false },
  { columnName: 'dataAvailable', sortingEnabled: false },
];

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Body = styled.div`
  display: flex;
  flex-direction: row;
`;

const TableContainer = styled.div`
  flex-grow: 10;
  margin-left: 32px;
  width: calc(100vw - 850px);
`;

const AccountUsersOverview: React.FunctionComponent<Props> = (props) => {
  const { account } = props;
  const [sorting, setSorting] = useLocalStorage<Sorting[]>('availableUsers-sort', [
    { columnName: 'email', direction: 'asc' as 'asc' },
  ]);
  const [page, setPage] = useLocalStorage<number>('availableUsers-page', 0);
  const [pageSize, setPageSize] = useLocalStorage<number>('availableUsers-pagesize', 10);
  const [columnWidths, setColumnWidths] = useState<Array<{ columnName: string; width: number | string }>>([
    { columnName: 'email', width: 240 },
    { columnName: 'emailVerified', width: 100 },
    { columnName: 'firstName', width: 150 },
    { columnName: 'lastName', width: 150 },
    { columnName: 'accountEnabled', width: 100 },
    { columnName: 'accountLocked', width: 100 },
    { columnName: 'roles', width: 180 },
    { columnName: 'resendInvite', width: 80 },
    { columnName: 'deleteUser', width: 80 },
  ]);
  const [search, setSearch] = useState<string | undefined>();
  const [user, setUser] = useState<User | null>();
  const [griddata, setGriddata] = useState<any>();
  const [count, setCount] = useState();
  const fetchTimeout = useRef<number>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchUsers = async () => {

    const result = await usersService.getUsers({ search, offset: page *pageSize, limit: pageSize, accountIds: [account] });

    setGriddata(result.items)
    setCount(result.total)
  }

  const onUserDeleted = (reload: Boolean) => {
    
    if (reload) {
      fetchUsers();
    }
  };

  let columns: any = [
    { name: 'email', title: 'E-mail', prio: 1 },
    { name: 'emailVerified', title: 'Verified', getCellValue: emailVerified, prio: 1 },
    { name: 'firstName', title: 'First name', prio: 2 },
    { name: 'lastName', title: 'Last name', prio: 2 },
    { name: 'accountEnabled', title: 'Enabled', getCellValue: accountEnabled, prio: 3 },
    { name: 'accountLocked', title: 'Locked', getCellValue: accountLocked, prio: 3 },
    { name: 'roles', title: 'Roles', getCellValue: roles, prio: 4 },
    { name: 'resendInvite', title: ' ', getCellValue: resendInviteValue, prio: 4 },
    { name: 'deleteUser', title: ' ', getCellValue: (row: User) => deleteUserValue(row, onUserDeleted), prio: 4 },
  ];
  const canWriteUsers = useHasPrivilege(Privilege.WriteUsers);

  const fetchDebounceUsers = () => {
    
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }

    fetchTimeout.current = setTimeout(() => {
      fetchUsers();
    }, 200)
  }

  useEffect(() => {


   if (!search) {
    fetchUsers();
   } 
   else {
    fetchDebounceUsers();
   }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, pageSize, account])

  return (
    <>
     {griddata && (<div className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5" style={{ overflow: 'visible' }}>
      {user && <CreateEditUser user={user} onClose={(reload) => {
        
          if (reload) {
            fetchUsers(); 
          }
          
          setUser(null);
        }} />}
      <TitleWrapper>
       <div style={{display: 'flex' }}>
          <CardHeader>Users</CardHeader>
          
          <div style={{ marginTop: '30px', marginLeft: '30px'}}>
            <UsersFilter key="filter" search={search} onSearchChange={(search) => {
              setSearch(search);
              setPage(0);
            }} />
          </div>
        </div>

        {canWriteUsers && (
          <AddCircleIcon
            color="primary"
            style={{ fontSize: 40, cursor: 'pointer' }}
            onClick={() => setUser({ id: '-1', roles: [], account: { id: props.account } })}
          />
        )}
      </TitleWrapper>
      <Body>
        <TableContainer style={{ marginLeft: '0px'}}>
          <Grid rows={griddata} columns={columns}>
            <PagingState
              currentPage={page}
              onCurrentPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
            <SortingState
              sorting={sorting}
              onSortingChange={setSorting}
              columnExtensions={sortingStateColumnExtensions}
            />
            <CustomPaging totalCount={count} />
            <Table
              rowComponent={(row) => (
                <Table.Row
                  {...row}
                  hover
                  // eslint-disable-next-line no-alert
                  onClick={() => canWriteUsers && setUser(row.row)}
                />
              )}
            />
            <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={setColumnWidths} />
            <TableHeaderRow showSortingControls />
            <PagingPanel pageSizes={[10, 25, 50]} />
          </Grid>
        </TableContainer>
      </Body>
    </div>)}
    </>
  );
};

export default withRouter(AccountUsersOverview);
